/**
 * Leaderboard hook — fetches and ranks teams by game type.
 * Subscribes to match results and team rating events.
 */

import { useState, useEffect, useMemo } from "react";
import { useNostrSubscription } from "./useNostr";
import {
  KIND_MATCH_RESULT,
  KIND_TEAM_RATINGS,
  GAME_TICTACTOE,
  TAG_GAME_TYPE,
  TAG_TEAM_A,
  TAG_TEAM_B,
  TAG_WINNER,
  TAG_RATING,
} from "@/lib/gameKinds";
import { createRating, type TeamRating } from "@/lib/ratings";
import type { NostrEvent } from "@nostrify/types";

export interface LeaderboardEntry {
  teamId: string;
  teamName: string;
  rating: TeamRating;
  rank: number;
}

export type LeaderboardPeriod = "all" | "monthly" | "weekly";

export function useLeaderboard(gameType: string = GAME_TICTACTOE) {
  const { subscribe } = useNostrSubscription();
  const [entries, setEntries] = useState<Map<string, LeaderboardEntry>>(new Map());
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);

    const cleanup = subscribe(
      [
        {
          kinds: [KIND_TEAM_RATINGS],
          "#g": [gameType],
          limit: 50,
        },
        {
          kinds: [KIND_MATCH_RESULT],
          "#g": [gameType],
          limit: 100,
        },
      ],
      (event: NostrEvent) => {
        if (event.kind === KIND_TEAM_RATINGS) {
          try {
            const data = JSON.parse(event.content);
            const dTag = event.tags.find((t) => t[0] === "d")?.[1] ?? "";
            const teamId = dTag.split(":")[0];
            const rating: TeamRating = {
              rating: data.rating ?? 1200,
              gamesPlayed: data.gamesPlayed ?? 0,
              wins: data.wins ?? 0,
              losses: data.losses ?? 0,
              draws: data.draws ?? 0,
            };

            setEntries((prev) => {
              const next = new Map(prev);
              next.set(teamId, {
                teamId,
                teamName: data.teamName ?? teamId.slice(0, 8),
                rating,
                rank: 0,
              });
              return next;
            });
          } catch {
            // Ignore parse errors
          }
        }

        if (event.kind === KIND_MATCH_RESULT) {
          // Update entries based on match results
          const teamA = event.tags.find((t) => t[0] === TAG_TEAM_A)?.[1];
          const teamB = event.tags.find((t) => t[0] === TAG_TEAM_B)?.[1];
          const winner = event.tags.find((t) => t[0] === TAG_WINNER)?.[1];

          if (!teamA || !teamB) return;

          setEntries((prev) => {
            const next = new Map(prev);

            // Update Team A
            const entryA = next.get(teamA) ?? {
              teamId: teamA,
              teamName: teamA.slice(0, 8),
              rating: createRating(),
              rank: 0,
            };
            entryA.rating.gamesPlayed++;
            if (winner === "a") entryA.rating.wins++;
            else if (winner === "b") entryA.rating.losses++;
            else entryA.rating.draws++;
            next.set(teamA, entryA);

            // Update Team B
            const entryB = next.get(teamB) ?? {
              teamId: teamB,
              teamName: teamB.slice(0, 8),
              rating: createRating(),
              rank: 0,
            };
            entryB.rating.gamesPlayed++;
            if (winner === "b") entryB.rating.wins++;
            else if (winner === "a") entryB.rating.losses++;
            else entryB.rating.draws++;
            next.set(teamB, entryB);

            return next;
          });
        }
      },
    );

    // Set loading false after a short delay to allow initial data to arrive
    const timer = setTimeout(() => setIsLoading(false), 3000);
    return () => {
      cleanup();
      clearTimeout(timer);
    };
  }, [gameType, subscribe]);

  // Sort by rating and assign ranks
  const ranked = useMemo(() => {
    const sorted = Array.from(entries.values())
      .sort((a, b) => b.rating.rating - a.rating.rating)
      .map((entry, index) => ({ ...entry, rank: index + 1 }));
    return sorted;
  }, [entries]);

  return {
    entries: ranked,
    isLoading,
  };
}